<template>
    <section ref="accordionRef" class="accordion">
        <div v-for="(item, index) in items" :key="index" class="accordion__item">
            <button :class="['accordion__header', {'active' : isActive(index)}]" :aria-expanded="isActive(index)" @click="toggle(index)"
                :aria-controls="'accordion-content-' + index" :id="'accordion-header-' + index">
                <span class="accordion__title">{{ item.title }}</span>
                <span class="accordion__icon" aria-hidden="true">
                    <svg :class="['accordion__icon-arrow', {'active' : isActive(index)}]">
                        <use xlink:href="#icon-prev"></use>
                    </svg>
                </span>
            </button>
            <div v-if="isActive(index)" class="accordion__body" :id="'accordion-content-' + index"
                :aria-labelledby="'accordion-header-' + index">
                <slot :name="item.key" :item="item"></slot>
                <slot v-if="$slots.default && !$slots[item.key]" :item="item"></slot>
            </div>
        </div>
    </section>
</template>

<script setup>
import { ref, defineProps, watch, nextTick } from 'vue';

const props = defineProps({
    items: {
        type: Array,
        required: true,
        default: () => []
    },
    modelValue: {
        type: Number,
        default: null
    },
    isAccordionOpenByDefault: {
        type: Boolean,
        default: false
    },
    singleToggleMode: {
        type: Boolean,
        default: false
    },
    isScrollToView: {
        type: Boolean,
        default: false  
    }
});

const activeIndexArray = ref([]);
const accordionRef = ref();

// Initialize default open state
if (props.isAccordionOpenByDefault && props.items.length > 0) {
    activeIndexArray.value = props.singleToggleMode ? [0] : Array.from({ length: props.items.length }, (_, i) => i);
}

const toggle = (index) => {
    const isOpen = activeIndexArray.value.includes(index);

    if (props.singleToggleMode) {
        activeIndexArray.value = isOpen ? [] : [index];
    } else {
        if (isOpen) {
            activeIndexArray.value = activeIndexArray.value.filter(i => i !== index);
        } else {
            activeIndexArray.value.push(index);
        }
    }

    // Scroll to the view
    if (props.isScrollToView) {
        nextTick(() => {
            const headerEls = document.querySelectorAll('.accordion__header');
            const header = document.getElementById('site-header');
            const headerHeight = header?.offsetHeight || 0;
            const target = isOpen
                ? accordionRef.value // if closing, scroll to full accordion
                : headerEls[index]; // if opening, scroll to specific header

            if (target) {
                const offset = headerHeight + 10; // adding some extra offset for better space;
                const scrollTop = target.getBoundingClientRect().top + window.scrollY - offset;

                window.scrollTo({
                    top: scrollTop,
                    behavior: 'smooth'
                });
            }
        });
    }
};

const isActive = (index) => activeIndexArray.value.includes(index);

// Watch for items change to reinitialize if needed
watch(() => props.items, (newItems) => {
    if (props.isAccordionOpenByDefault && newItems.length > 0) {
        activeIndexArray.value = props.singleToggleMode ? [0] : Array.from({ length: newItems.length }, (_, i) => i);
    } else {
        activeIndexArray.value = [];
    }
});

</script>
